import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  Download,
  Storage,
  SmartToy,
  MenuBook,
  Business,
  CloudDownload,
} from "@mui/icons-material";
import { auth } from "@/lib/firebase";
import { brand } from "@/theme";

interface ExportOption {
  label: string;
  description: string;
  icon: React.ReactNode;
  endpoint: string;
  filename: string;
}

const exports: ExportOption[] = [
  {
    label: "Full Database Backup",
    description:
      "Complete PostgreSQL dump (SQL format). Includes all tables, data, and schema. Use with psql or pg_restore to recreate the entire database.",
    icon: <Storage fontSize="large" />,
    endpoint: "/api/v1/backup/database",
    filename: "mastermind_backup.sql",
  },
  {
    label: "Full Data Export",
    description:
      "All agents, KB articles, and CRM data in a single JSON file. Human-readable and portable — can be used to seed a new instance.",
    icon: <CloudDownload fontSize="large" />,
    endpoint: "/api/v1/backup/export/all",
    filename: "mastermind_full_export.json",
  },
];

const granularExports: ExportOption[] = [
  {
    label: "Agents",
    description: "All 6 agent character sheets with personality, goals, skills, model config.",
    icon: <SmartToy />,
    endpoint: "/api/v1/backup/export/agents",
    filename: "agents_export.json",
  },
  {
    label: "Knowledge Base",
    description: "All KB articles including markdown content, categories, tags.",
    icon: <MenuBook />,
    endpoint: "/api/v1/backup/export/kb",
    filename: "kb_export.json",
  },
  {
    label: "CRM",
    description: "Companies, individuals, communications, and follow-ups.",
    icon: <Business />,
    endpoint: "/api/v1/backup/export/crm",
    filename: "crm_export.json",
  },
];

export default function Backup() {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const handleDownload = async (option: ExportOption) => {
    setDownloading(option.endpoint);
    try {
      const user = auth.currentUser;
      const headers: Record<string, string> = {};
      if (user) {
        const token = await user.getIdToken();
        headers["Authorization"] = `Bearer ${token}`;
      } else {
        headers["X-Dev-User-Id"] = "dev-steve";
      }

      const response = await fetch(option.endpoint, { headers });
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = option.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: `${option.label} downloaded successfully` });
    } catch {
      setSnackbar({ open: true, message: `Failed to download ${option.label}` });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Backup & Export
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Download backups of your data for disaster recovery. Store these securely — they contain all
        your business data.
      </Typography>

      {/* Full backups */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Full Backups
      </Typography>
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 4 }}
      >
        {exports.map((option) => (
          <Card key={option.endpoint} sx={{ borderLeft: `4px solid ${brand.forestGreen}` }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ color: brand.forestGreen, mt: 0.5 }}>{option.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {option.description}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownload(option)}
                    disabled={downloading === option.endpoint}
                  >
                    {downloading === option.endpoint ? "Downloading..." : "Download"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Granular exports */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Granular Exports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Download specific modules as JSON files.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 2,
          mb: 4,
        }}
      >
        {granularExports.map((option) => (
          <Card key={option.endpoint}>
            <CardContent sx={{ textAlign: "center" }}>
              <Box sx={{ color: brand.forestGreen, mb: 1 }}>{option.icon}</Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {option.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                {option.description}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={() => handleDownload(option)}
                disabled={downloading === option.endpoint}
              >
                {downloading === option.endpoint ? "..." : "Export"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Alert severity="info">
        <Typography variant="body2" fontWeight={600}>
          Backup recommendations:
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2 }}>
          <li>Download a full backup weekly and before any major changes</li>
          <li>Store backups in a separate location (Google Drive, external storage)</li>
          <li>
            The SQL dump can restore the entire database with:{" "}
            <code>psql -U mastermind -d mastermind &lt; backup.sql</code>
          </li>
          <li>JSON exports are human-readable and can be used to seed a new instance</li>
          <li>In production, Cloud SQL automated backups will run daily</li>
        </Typography>
      </Alert>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
}
